import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  getClubByUser,
  getClubByTeacher,
  getClubById,
} from "../../../services/clubServices";
import attendanceServices from "../../../services/attendanceServices";
import {
  getDojosByClub,
  getDojosByTeacher,
} from "../../../services/dojoServices";
import {
  getGroupsByDojo,
  getGroupsByTeacher,
} from "../../../services/groupServices";
import { getTeacherByUser } from "../../../services/teacherServices";
import { isDemoUser } from "./../../../utils/isDemoUser";

import {
  FaCalendarAlt,
  FaChartPie,
  FaCheckCircle,
  FaLayerGroup,
  FaMapMarkerAlt,
  FaMedkit,
  FaPercentage,
  FaTimesCircle,
  FaUserCheck,
  FaUsers,
} from "react-icons/fa";

import StatCard from "./StatCard";
import PresencePieChart from "./PresencePieChart";
import PresenceBarChart from "./PresenceBarChart";

function cn(...classes) {
  return classes.filter(Boolean).join(" ");
}

function formatMonthLabel(month) {
  if (!month) return "";
  return new Date(`${month}-01`).toLocaleDateString("fr-FR", {
    month: "long",
    year: "numeric",
  });
}

function getGroupDojoId(group) {
  return String(group?.dojo_id ?? group?.dojo?.id ?? "");
}

function SmallKpiCard({ icon, label, value, colorClass = "" }) {
  return (
    <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4 dark:border-slate-800 dark:bg-slate-800/60">
      <div
        className={cn(
          "mb-3 flex h-10 w-10 items-center justify-center rounded-xl text-base",
          colorClass
        )}
      >
        {icon}
      </div>

      <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500 dark:text-slate-400">
        {label}
      </p>

      <p className="mt-2 text-2xl font-black text-slate-900 dark:text-slate-100">
        {value}
      </p>
    </div>
  );
}

function DojoCard({ dojo, active, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        "w-full rounded-[22px] border p-4 text-left shadow-sm transition",
        active
          ? "border-blue-500 bg-blue-50 ring-2 ring-blue-100 dark:border-blue-400 dark:bg-blue-500/10 dark:ring-blue-500/20"
          : "border-slate-200 bg-white hover:border-slate-300 hover:shadow-md dark:border-slate-800 dark:bg-slate-900 dark:hover:border-slate-700"
      )}
    >
      <div className="flex items-center gap-4">
        {dojo?.logo_dojo ? (
          <img
            src={dojo.logo_dojo}
            alt={dojo.nom_dojo}
            className="h-16 w-16 rounded-2xl object-cover"
          />
        ) : (
          <div className="flex h-16 w-16 items-center justify-center rounded-2xl bg-slate-100 text-slate-500 dark:bg-slate-800 dark:text-slate-300">
            <FaMapMarkerAlt className="text-xl" />
          </div>
        )}

        <div className="min-w-0">
          <h3 className="text-base font-black text-slate-900 dark:text-slate-100">
            {dojo.nom_dojo}
          </h3>
          <p className="mt-1 line-clamp-2 text-sm text-slate-600 dark:text-slate-400">
            {dojo.adresse_dojo || "Adresse non renseignée"}
          </p>
        </div>
      </div>
    </button>
  );
}

function GroupCard({ group, active, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        "w-full rounded-[22px] border p-4 text-left shadow-sm transition",
        active
          ? "border-emerald-500 bg-emerald-50 ring-2 ring-emerald-100 dark:border-emerald-400 dark:bg-emerald-500/10 dark:ring-emerald-500/20"
          : "border-slate-200 bg-white hover:border-slate-300 hover:shadow-md dark:border-slate-800 dark:bg-slate-900 dark:hover:border-slate-700"
      )}
    >
      <p className="text-base font-black text-slate-900 dark:text-slate-100">
        {group.nom_groupe}
      </p>
      <p className="mt-1 line-clamp-2 text-sm text-slate-600 dark:text-slate-400">
        {group.description_groupe || "Aucune description"}
      </p>
    </button>
  );
}

const StatistiquesAttendances = () => {
  const navigate = useNavigate();

  let userInfo = null;
  try {
    userInfo = JSON.parse(localStorage.getItem("userInfo") || "null");
  } catch (error) {
    userInfo = null;
  }

  const userId = userInfo?.userId;
  const roleId = Number(userInfo?.role_id || 0);
  const demoMode = isDemoUser(userInfo);
  const isTeacher = roleId === 6;

  const [club, setClub] = useState(null);
  const [dojos, setDojos] = useState([]);
  const [allGroups, setAllGroups] = useState([]);
  const [teacher, setTeacher] = useState(null);
  const [teacherAccessMap, setTeacherAccessMap] = useState({});

  const [selectedDojo, setSelectedDojo] = useState("");
  const [selectedGroup, setSelectedGroup] = useState("");
  const [selectedMonth, setSelectedMonth] = useState(
    new Date().toISOString().slice(0, 7)
  );

  const [loading, setLoading] = useState(true);
  const [statsLoading, setStatsLoading] = useState(false);
  const [attendanceStats, setAttendanceStats] = useState(null);

  useEffect(() => {
    if (userId) {
      fetchAccessData();
    } else {
      setLoading(false);
    }
  }, [userId]);

  const fetchAccessData = async () => {
    try {
      setLoading(true);

      let clubData = null;
      let dojosData = [];
      let groupsData = [];
      let teacherData = null;
      let accessMap = {};

      if (demoMode) {
        clubData = await getClubById(2);
        dojosData = (await getDojosByClub(2)) || [];
        groupsData = (
          await Promise.all(dojosData.map((dojo) => getGroupsByDojo(dojo.id)))
        ).flat();
      } else if (isTeacher) {
        teacherData = await getTeacherByUser(userId);

        if (teacherData?.id) {
          clubData = await getClubByTeacher(teacherData.id);
          dojosData = (await getDojosByTeacher(teacherData.id)) || [];
          groupsData = (await getGroupsByTeacher(teacherData.id)) || [];

          dojosData.forEach((dojo) => {
            accessMap[dojo.id] = groupsData.filter(
              (group) => String(group.dojo_id) === String(dojo.id)
            );
          });
        }
      } else {
        clubData = await getClubByUser(userId);

        if (clubData?.id) {
          dojosData = (await getDojosByClub(clubData.id)) || [];
          groupsData = (
            await Promise.all(dojosData.map((dojo) => getGroupsByDojo(dojo.id)))
          ).flat();
        }
      }

      setClub(clubData?.id ? clubData : null);
      setTeacher(teacherData || null);
      setDojos(Array.isArray(dojosData) ? dojosData : []);
      setAllGroups(Array.isArray(groupsData) ? groupsData : []);
      setTeacherAccessMap(accessMap);

      if (Array.isArray(dojosData) && dojosData.length === 1) {
        setSelectedDojo(String(dojosData[0].id));
      }
    } catch (error) {
      console.error("Erreur lors du chargement des données :", error);
      setClub(null);
      setTeacher(null);
      setDojos([]);
      setAllGroups([]);
      setTeacherAccessMap({});
    } finally {
      setLoading(false);
    }
  };

  const availableGroups = useMemo(() => {
    if (!selectedDojo) return [];

    if (isTeacher) {
      return teacherAccessMap[selectedDojo] || [];
    }

    return allGroups.filter(
      (group) => getGroupDojoId(group) === String(selectedDojo)
    );
  }, [selectedDojo, isTeacher, teacherAccessMap, allGroups]);

  useEffect(() => {
    if (!selectedDojo) {
      setSelectedGroup("");
      setAttendanceStats(null);
      return;
    }

    const hasSelectedGroup = availableGroups.some(
      (group) => String(group.id) === String(selectedGroup)
    );

    if (!hasSelectedGroup) {
      setSelectedGroup("");
      setAttendanceStats(null);
    }

    if (availableGroups.length === 1 && !selectedGroup) {
      setSelectedGroup(String(availableGroups[0].id));
    }
  }, [selectedDojo, availableGroups, selectedGroup]);

  useEffect(() => {
    if (selectedGroup) {
      fetchAttendanceStats(selectedGroup, selectedMonth);
    } else {
      setAttendanceStats(null);
    }
  }, [selectedGroup, selectedMonth]);

  const fetchAttendanceStats = async (groupId, month) => {
    try {
      setStatsLoading(true);

      const attendances = await attendanceServices.getGroupAttendances(
        groupId,
        month
      );

      let totalPresences = 0;
      let totalAbsences = 0;
      let totalSick = 0;
      let totalEntries = 0;

      const attendanceByDate = {};
      const attendanceByDayNumber = {};

      (Array.isArray(attendances) ? attendances : []).forEach((entry) => {
        const date = String(entry?.date_presence || "").split("T")[0];
        if (!date) return;

        let presences = {};

        if (typeof entry?.presences === "string") {
          try {
            presences = JSON.parse(entry.presences) || {};
          } catch (error) {
            presences = {};
          }
        } else if (
          entry?.presences &&
          typeof entry.presences === "object" &&
          !Array.isArray(entry.presences)
        ) {
          presences = entry.presences;
        }

        if (!attendanceByDate[date]) {
          attendanceByDate[date] = {
            date,
            Présents: 0,
            Absents: 0,
            "Malades/Blessés": 0,
          };
        }

        Object.values(presences).forEach((status) => {
          totalEntries += 1;

          if (status === "Présent") {
            totalPresences += 1;
            attendanceByDate[date]["Présents"] += 1;
          }

          if (status === "Absent") {
            totalAbsences += 1;
            attendanceByDate[date]["Absents"] += 1;
          }

          if (status === "Malade/Blessé") {
            totalSick += 1;
            attendanceByDate[date]["Malades/Blessés"] += 1;
          }
        });
      });

      Object.values(attendanceByDate).forEach((item) => {
        const dayLabel = item.date.split("-")[2];
        attendanceByDayNumber[item.date] = {
          ...item,
          label: dayLabel,
        };
      });

      const presenceRate =
        totalEntries > 0 ? ((totalPresences / totalEntries) * 100).toFixed(1) : "0";
      const absenceRate =
        totalEntries > 0 ? ((totalAbsences / totalEntries) * 100).toFixed(1) : "0";
      const sickRate =
        totalEntries > 0 ? ((totalSick / totalEntries) * 100).toFixed(1) : "0";

      const sortedAttendanceByDate = Object.values(attendanceByDayNumber).sort(
        (a, b) => a.date.localeCompare(b.date)
      );

      const activeDaysCount = sortedAttendanceByDate.filter(
        (day) =>
          day["Présents"] > 0 ||
          day["Absents"] > 0 ||
          day["Malades/Blessés"] > 0
      ).length;

      setAttendanceStats({
        presenceRate,
        absenceRate,
        sickRate,
        totalPresences,
        totalAbsences,
        totalSick,
        totalEntries,
        activeDaysCount,
        attendanceByDate: sortedAttendanceByDate,
        globalDistribution: [
          { name: "Présents", value: totalPresences },
          { name: "Absents", value: totalAbsences },
          { name: "Malades/Blessés", value: totalSick },
        ],
      });
    } catch (error) {
      console.error("Erreur lors du chargement des statistiques :", error);
      setAttendanceStats(null);
    } finally {
      setStatsLoading(false);
    }
  };

  const accessibleDojosCount = useMemo(() => {
    if (!isTeacher) return dojos.length;

    return Object.values(teacherAccessMap).filter(
      (groupsList) => Array.isArray(groupsList) && groupsList.length > 0
    ).length;
  }, [isTeacher, dojos.length, teacherAccessMap]);

  const selectedGroupObject = useMemo(() => {
    return availableGroups.find(
      (group) => String(group.id) === String(selectedGroup)
    );
  }, [availableGroups, selectedGroup]);

  return (
    <div className="w-full bg-slate-50 pb-12 pt-[100px] dark:bg-slate-950">
      <div className="mx-auto max-w-[1400px] px-4 sm:px-6">
        {/* Header */}
        <section className="mt-4 rounded-[28px] border border-slate-200 bg-white p-5 shadow-sm dark:border-slate-800 dark:bg-slate-900 sm:p-6 lg:p-8">
          <div className="flex flex-col gap-5 lg:flex-row lg:items-center lg:justify-between">
            <div>
              <p className="text-sm font-semibold uppercase tracking-wider text-blue-600 dark:text-blue-400">
                Suivi de présence
              </p>
              <h1 className="mt-1 text-3xl font-black text-slate-900 dark:text-slate-100">
                Statistiques
              </h1>
              <p className="mt-2 max-w-3xl text-sm leading-6 text-slate-600 dark:text-slate-400">
                Analyse les taux de présence, d’absence et d’indisponibilité par
                groupe et par mois.
              </p>
            </div>

            <button
              onClick={() => navigate("/")}
              className="inline-flex items-center justify-center rounded-2xl border border-slate-200 bg-white px-4 py-3 font-semibold text-slate-700 transition hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-100 dark:hover:bg-slate-700"
            >
              Accueil
            </button>
          </div>
        </section>

        {/* Club + KPI */}
        <section className="mt-5">
          {loading ? (
            <div className="grid gap-4 lg:grid-cols-[280px_minmax(0,1fr)]">
              <div className="animate-pulse overflow-hidden rounded-[24px] border border-slate-200 bg-white shadow-sm dark:border-slate-800 dark:bg-slate-900">
                <div className="h-24 bg-slate-200 dark:bg-slate-800" />
                <div className="flex items-center gap-3 p-4">
                  <div className="h-14 w-14 rounded-full bg-slate-200 dark:bg-slate-800" />
                  <div className="flex-1">
                    <div className="h-4 w-32 rounded bg-slate-200 dark:bg-slate-800" />
                    <div className="mt-2 h-3 w-40 rounded bg-slate-200 dark:bg-slate-800" />
                  </div>
                </div>
              </div>

              <div className="animate-pulse rounded-[24px] border border-slate-200 bg-white p-4 shadow-sm dark:border-slate-800 dark:bg-slate-900">
                <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
                  <div className="h-28 rounded-2xl bg-slate-200 dark:bg-slate-800" />
                  <div className="h-28 rounded-2xl bg-slate-200 dark:bg-slate-800" />
                  <div className="h-28 rounded-2xl bg-slate-200 dark:bg-slate-800" />
                  <div className="h-28 rounded-2xl bg-slate-200 dark:bg-slate-800" />
                </div>
              </div>
            </div>
          ) : club ? (
            <div className="grid gap-4 lg:grid-cols-[280px_minmax(0,1fr)]">
              <div className="overflow-hidden rounded-[24px] border border-slate-200 bg-white shadow-sm dark:border-slate-800 dark:bg-slate-900">
                <div className="bg-gradient-to-br from-slate-950 via-slate-900 to-blue-900 px-5 py-4 text-white">
                  <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-white/70">
                    Club
                  </p>
                  <h2 className="mt-2 text-xl font-black leading-tight">
                    {club.nom_club}
                  </h2>
                  <p className="mt-1 text-sm text-white/80">
                    {club.departement_club || "Département non renseigné"}
                  </p>
                </div>

                <div className="flex items-center gap-3 p-4">
                  {club.logo_url ? (
                    <img
                      src={club.logo_url}
                      alt={club.nom_club}
                      className="h-16 w-16 rounded-full border-2 border-white object-cover shadow-lg ring-1 ring-slate-200 dark:border-slate-900 dark:ring-slate-700"
                    />
                  ) : (
                    <div className="flex h-16 w-16 items-center justify-center rounded-full bg-slate-100 text-slate-500 shadow-lg dark:bg-slate-800 dark:text-slate-300">
                      <FaUsers className="text-2xl" />
                    </div>
                  )}

                  <div className="min-w-0 flex-1">
                    <p className="line-clamp-3 text-sm leading-5 text-slate-600 dark:text-slate-400">
                      {club.adresse_club || "Adresse non renseignée"}
                    </p>
                  </div>
                </div>
              </div>

              <div className="rounded-[24px] border border-slate-200 bg-white p-4 shadow-sm dark:border-slate-800 dark:bg-slate-900">
                <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
                  <SmallKpiCard
                    icon={<FaMapMarkerAlt />}
                    label="Lieux"
                    value={accessibleDojosCount}
                    colorClass="bg-blue-50 text-blue-700 dark:bg-blue-500/10 dark:text-blue-300"
                  />

                  <SmallKpiCard
                    icon={<FaLayerGroup />}
                    label="Groupes"
                    value={selectedDojo ? availableGroups.length : allGroups.length}
                    colorClass="bg-purple-50 text-purple-700 dark:bg-purple-500/10 dark:text-purple-300"
                  />

                  <SmallKpiCard
                    icon={<FaCalendarAlt />}
                    label="Mois"
                    value={formatMonthLabel(selectedMonth)}
                    colorClass="bg-amber-50 text-amber-700 dark:bg-amber-500/10 dark:text-amber-300"
                  />

                  <SmallKpiCard
                    icon={<FaChartPie />}
                    label="Séances"
                    value={attendanceStats?.activeDaysCount || 0}
                    colorClass="bg-emerald-50 text-emerald-700 dark:bg-emerald-500/10 dark:text-emerald-300"
                  />
                </div>

                <div className="mt-4 rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm dark:border-slate-800 dark:bg-slate-800/60">
                  {isTeacher ? (
                    <div className="flex flex-wrap items-center gap-2 text-slate-700 dark:text-slate-300">
                      <span className="inline-flex items-center gap-2 rounded-full bg-white px-3 py-1 font-semibold text-slate-800 dark:bg-slate-900 dark:text-slate-100">
                        <FaUserCheck />
                        Accès enseignant
                      </span>
                      <span>
                        {teacher?.prenom} {teacher?.nom} · {accessibleDojosCount} lieu(x)
                      </span>
                    </div>
                  ) : (
                    <div className="flex flex-wrap items-center gap-2 text-slate-700 dark:text-slate-300">
                      <span className="inline-flex items-center gap-2 rounded-full bg-white px-3 py-1 font-semibold text-slate-800 dark:bg-slate-900 dark:text-slate-100">
                        <FaUserCheck />
                        Accès complet
                      </span>
                      <span>
                        Vous pouvez consulter les statistiques de tous les lieux et groupes.
                      </span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          ) : null}
        </section>

        {/* Toolbar */}
        <section className="mt-6 rounded-[24px] border border-slate-200 bg-white p-5 shadow-sm dark:border-slate-800 dark:bg-slate-900">
          <div className="flex flex-col gap-4 xl:flex-row xl:items-center xl:justify-between">
            <div>
              <label className="mb-2 block text-sm font-semibold text-slate-800 dark:text-slate-200">
                Mois analysé
              </label>
              <input
                type="month"
                value={selectedMonth}
                onChange={(e) => setSelectedMonth(e.target.value)}
                className="rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-900 outline-none transition focus:border-blue-500 focus:ring-4 focus:ring-blue-100 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-100 dark:focus:border-blue-400 dark:focus:ring-blue-500/20"
              />
            </div>

            <div className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-600 dark:border-slate-800 dark:bg-slate-800/60 dark:text-slate-300">
              {selectedGroupObject ? (
                <>
                  Groupe sélectionné :{" "}
                  <span className="font-bold text-slate-900 dark:text-slate-100">
                    {selectedGroupObject.nom_groupe}
                  </span>
                </>
              ) : (
                "Sélectionne un lieu puis un groupe pour afficher les statistiques."
              )}
            </div>
          </div>
        </section>

        {/* Lieux */}
        <section className="mt-6 rounded-[24px] border border-slate-200 bg-white p-5 shadow-sm dark:border-slate-800 dark:bg-slate-900">
          <div className="mb-4 flex items-center gap-2">
            <FaMapMarkerAlt className="text-blue-600 dark:text-blue-400" />
            <h2 className="text-2xl font-black text-slate-900 dark:text-slate-100">
              Lieux
            </h2>
          </div>

          <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
            {dojos.map((dojo) => (
              <DojoCard
                key={dojo.id}
                dojo={dojo}
                active={String(selectedDojo) === String(dojo.id)}
                onClick={() => {
                  setSelectedDojo(String(dojo.id));
                  setSelectedGroup("");
                  setAttendanceStats(null);
                }}
              />
            ))}
          </div>
        </section>

        {/* Groupes */}
        {selectedDojo && (
          <section className="mt-6 rounded-[24px] border border-slate-200 bg-white p-5 shadow-sm dark:border-slate-800 dark:bg-slate-900">
            <div className="mb-4 flex items-center gap-2">
              <FaLayerGroup className="text-emerald-600 dark:text-emerald-400" />
              <h2 className="text-2xl font-black text-slate-900 dark:text-slate-100">
                Groupes
              </h2>
            </div>

            {availableGroups.length > 0 ? (
              <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
                {availableGroups.map((group) => (
                  <GroupCard
                    key={group.id}
                    group={group}
                    active={String(selectedGroup) === String(group.id)}
                    onClick={() => setSelectedGroup(String(group.id))}
                  />
                ))}
              </div>
            ) : (
              <div className="rounded-2xl border border-slate-200 bg-slate-50 p-6 text-center text-slate-500 dark:border-slate-800 dark:bg-slate-800/60 dark:text-slate-400">
                Aucun groupe disponible pour ce lieu.
              </div>
            )}
          </section>
        )}

        {/* Stats cards */}
        {selectedGroup && (
          <section className="mt-6">
            <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
              <StatCard
                title="Présences moyennes"
                value={attendanceStats?.presenceRate || "0"}
                icon={<FaCheckCircle className="text-green-500" />}
                color="border-green-500"
              />

              <StatCard
                title="Absences moyennes"
                value={attendanceStats?.absenceRate || "0"}
                icon={<FaTimesCircle className="text-red-500" />}
                color="border-red-500"
              />

              <StatCard
                title="Malades / Blessés"
                value={attendanceStats?.sickRate || "0"}
                icon={<FaMedkit className="text-yellow-500" />}
                color="border-yellow-500"
              />
            </div>

            <div className="mt-4 grid gap-4 md:grid-cols-3">
              <SmallKpiCard
                icon={<FaCheckCircle />}
                label="Présences"
                value={attendanceStats?.totalPresences || 0}
                colorClass="bg-emerald-50 text-emerald-700 dark:bg-emerald-500/10 dark:text-emerald-300"
              />

              <SmallKpiCard
                icon={<FaTimesCircle />}
                label="Absences"
                value={attendanceStats?.totalAbsences || 0}
                colorClass="bg-red-50 text-red-700 dark:bg-red-500/10 dark:text-red-300"
              />

              <SmallKpiCard
                icon={<FaPercentage />}
                label="Entrées"
                value={attendanceStats?.totalEntries || 0}
                colorClass="bg-indigo-50 text-indigo-700 dark:bg-indigo-500/10 dark:text-indigo-300"
              />
            </div>
          </section>
        )}

        {/* Graphiques */}
        {selectedGroup && (
          <section className="mt-8 grid grid-cols-1 gap-6 xl:grid-cols-2">
            <div className="rounded-[24px] border border-slate-200 bg-white p-5 shadow-sm dark:border-slate-800 dark:bg-slate-900">
              {statsLoading ? (
                <div className="flex h-[320px] items-center justify-center text-slate-500 dark:text-slate-400">
                  Chargement du graphique...
                </div>
              ) : attendanceStats?.globalDistribution?.some(
                  (item) => item.value > 0
                ) ? (
                <PresencePieChart data={attendanceStats.globalDistribution} />
              ) : (
                <div className="flex h-[320px] items-center justify-center text-center text-slate-500 dark:text-slate-400">
                  Aucune donnée statistique disponible pour ce mois.
                </div>
              )}
            </div>

            <div className="rounded-[24px] border border-slate-200 bg-white p-5 shadow-sm dark:border-slate-800 dark:bg-slate-900">
              {statsLoading ? (
                <div className="flex h-[320px] items-center justify-center text-slate-500 dark:text-slate-400">
                  Chargement du graphique...
                </div>
              ) : attendanceStats?.attendanceByDate?.length ? (
                <PresenceBarChart data={attendanceStats.attendanceByDate} />
              ) : (
                <div className="flex h-[320px] items-center justify-center text-center text-slate-500 dark:text-slate-400">
                  Aucun historique à afficher pour ce mois.
                </div>
              )}
            </div>
          </section>
        )}
      </div>
    </div>
  );
};

export default StatistiquesAttendances;